import { useEffect, useRef } from 'react';

interface SeoHeadProps {
  title?: string;
  description?: string;
  keywords?: string;
  canonical?: string;
  ogTitle?: string;
  ogDescription?: string;
  ogUrl?: string;
  jsonLd?: Record<string, unknown> | Record<string, unknown>[];
}

export default function SeoHead({
  title,
  description,
  keywords,
  canonical,
  ogTitle,
  ogDescription,
  ogUrl,
  jsonLd,
}: SeoHeadProps) {
  const scriptRefs = useRef<HTMLScriptElement[]>([]);
  const prevTitle = useRef(document.title);

  useEffect(() => {
    if (title) {
      prevTitle.current = document.title;
      document.title = title;
    }

    const createdMeta: HTMLMetaElement[] = [];
    const createdLink: HTMLLinkElement[] = [];

    const setMeta = (attrName: string, attrValue: string, selector: string, attr: string) => {
      let el = document.querySelector<HTMLMetaElement>(selector);
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute(attrName, attrValue);
        document.head.appendChild(el);
        createdMeta.push(el);
      }
      return el;
    };

    if (description) setMeta('name', 'description', 'meta[name="description"]', 'name').setAttribute('content', description);
    if (keywords) setMeta('name', 'keywords', 'meta[name="keywords"]', 'name').setAttribute('content', keywords);
    if (ogTitle) {
      const el = setMeta('property', 'og:title', 'meta[property="og:title"]', 'property');
      el.setAttribute('content', ogTitle);
    }
    if (ogDescription) {
      const el = setMeta('property', 'og:description', 'meta[property="og:description"]', 'property');
      el.setAttribute('content', ogDescription);
    }
    if (ogUrl) {
      const el = setMeta('property', 'og:url', 'meta[property="og:url"]', 'property');
      el.setAttribute('content', ogUrl);
    }

    if (canonical) {
      let linkEl = document.querySelector<HTMLLinkElement>('link[rel="canonical"]');
      if (!linkEl) {
        linkEl = document.createElement('link');
        linkEl.setAttribute('rel', 'canonical');
        document.head.appendChild(linkEl);
        createdLink.push(linkEl);
      }
      linkEl.setAttribute('href', canonical);
    }

    // Clean up previous JSON-LD scripts
    scriptRefs.current.forEach((s) => {
      if (s.parentNode) s.parentNode.removeChild(s);
    });
    scriptRefs.current = [];

    // Inject new JSON-LD scripts
    if (jsonLd) {
      const schemas = Array.isArray(jsonLd) ? jsonLd : [jsonLd];
      schemas.forEach((schema) => {
        const scriptEl = document.createElement('script');
        scriptEl.type = 'application/ld+json';
        scriptEl.text = JSON.stringify(schema);
        document.head.appendChild(scriptEl);
        scriptRefs.current.push(scriptEl);
      });
    }

    return () => {
      // Restore previous title
      if (title) {
        document.title = prevTitle.current;
      }
      // Remove created elements
      createdMeta.forEach((el) => el.parentNode?.removeChild(el));
      createdLink.forEach((el) => el.parentNode?.removeChild(el));
      scriptRefs.current.forEach((s) => {
        if (s.parentNode) s.parentNode.removeChild(s);
      });
      scriptRefs.current = [];
    };
  }, [title, description, keywords, canonical, ogTitle, ogDescription, ogUrl, jsonLd]);

  return null;
}